import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { CircularProgress, Dialog, Paper, RadioGroup } from '@mui/material'
import DOMPurify from 'dompurify'
import html2canvas from 'html2canvas'
import { marked } from 'marked'

import { generateAITranscriptPdfBlob } from '~api/services/ai/AITranscriptPdf'
import { images } from '~assets/images'
import { Avatar, Button, Icon, IconButton, Radio, Typography } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { borderRadius, lightTokens, spacing, typography } from '~tokens/internal'
import {
  type AIInteractionId,
  type AIInteractionTranscript,
  type EnrichedOrganizationAISettings,
  type UserId,
} from '~types'
import { fetchFileBlob } from '~utils/fileUtils'

import { InteractionTypeIcon } from '../../components/InteractionTypeIcon'
import { getDisplayAttributes } from '../../constants/interactionTypeConstants'

import './ShareLogModalContent.scss'

const DEFAULT_CORNER_RADIUS = borderRadius.md

interface ShareLogModalProps {
  isOpen: boolean
  onClose: () => void
  aiInteractionId: string
  transcript: AIInteractionTranscript | null
  orgAiSettings: EnrichedOrganizationAISettings | null
}

type ExportFormat = 'pdf' | 'jpg'

interface SimpleMessage {
  id: string | null
  text: string
  sender: 'USER' | 'AGENT'
  timestamp: Date
}

const ShareLogModalContent: React.FC<ShareLogModalProps> = ({
  isOpen,
  onClose,
  aiInteractionId,
  transcript,
  orgAiSettings,
}) => {
  const [exportFormat, setExportFormat] = useState<ExportFormat>('jpg')
  const [isPdfGenerating, setIsPdfGenerating] = useState<boolean>(false)
  const [isCapturingJpg, setIsCapturingJpg] = useState<boolean>(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [jpgZoom, setJpgZoom] = useState<number>(100)
  const captureLogoDataUrl = useRef<string | null>(null)
  const captureLogoFetchPromise = useRef<Promise<void>>(Promise.resolve())
  const capturePoliLogoDataUrl = useRef<string | null>(null)
  const userId = useSelector((state: RootState) => state?.auth.user?.id)
  const latestRequestId = useRef<number>(0)
  const conversationRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  const messages = useMemo((): SimpleMessage[] => {
    if (!transcript) return []

    const msgs: SimpleMessage[] = []

    transcript.queries.forEach((query) => {
      msgs.push({
        id: query.id || null,
        text: query.body,
        sender: 'USER',
        timestamp: new Date(query.created || new Date()),
      })
    })

    transcript.responses.forEach((response) => {
      const text = marked.parse(response.body, { breaks: true }) as string
      msgs.push({
        id: response.id || null,
        text,
        sender: 'AGENT',
        timestamp: new Date(response.created || new Date()),
      })
    })

    return msgs.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }, [transcript])

  const userBgColor = orgAiSettings?.bg_color
    ? `#${orgAiSettings.bg_color}`
    : lightTokens.primary.main
  const userTextColor = orgAiSettings?.font_color ? `#${orgAiSettings.font_color}` : 'white'
  const cornerRadius = orgAiSettings?.corner_radius
    ? `${orgAiSettings.corner_radius}px`
    : DEFAULT_CORNER_RADIUS
  const fontFamily = orgAiSettings?.font_family || 'inherit'
  const linkColor = orgAiSettings?.link_color
    ? `#${orgAiSettings.link_color}`
    : lightTokens.primary.dark

  useEffect(() => {
    if (!isOpen || !aiInteractionId || !userId) return

    const generatePdf = async (): Promise<void> => {
      const currentRequestId = ++latestRequestId.current
      setIsPdfGenerating(true)

      try {
        const newPdfBlob = await generateAITranscriptPdfBlob({
          userId: userId as UserId,
          aiInteractionId: aiInteractionId as AIInteractionId,
        })

        if (currentRequestId !== latestRequestId.current) {
          return
        }

        const newPdfUrl = URL.createObjectURL(newPdfBlob)
        setPdfBlob(newPdfBlob)
        setPdfUrl(newPdfUrl)
      } catch (error) {
        if (currentRequestId === latestRequestId.current) {
          console.error('Error generating PDF:', error)
        }
      } finally {
        if (currentRequestId === latestRequestId.current) {
          setIsPdfGenerating(false)
        }
      }
    }

    generatePdf()
  }, [isOpen, aiInteractionId, userId])

  useEffect(() => {
    return (): void => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [pdfUrl])

  const prefetchAsDataUrl = (
    src: string,
    ref: React.MutableRefObject<string | null>,
  ): (() => void) => {
    ref.current = null
    let cancelled = false
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.onload = (): void => {
      if (cancelled) return
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth || 36
        canvas.height = img.naturalHeight || 36
        canvas.getContext('2d')?.drawImage(img, 0, 0)
        ref.current = canvas.toDataURL('image/png')
      } catch {
        ref.current = null
      }
    }
    img.onerror = (): void => {
      if (!cancelled) ref.current = null
    }
    const sep = src.includes('?') ? '&' : '?'
    img.src = `${src}${sep}_t=${Date.now()}`
    return () => {
      cancelled = true
    }
  }

  const fetchAsDataUrl = (
    src: string,
    ref: React.MutableRefObject<string | null>,
    promiseRef: React.MutableRefObject<Promise<void>>,
  ): (() => void) => {
    ref.current = null
    let cancelled = false
    promiseRef.current = new Promise<void>((resolve) => {
      fetchFileBlob(src)
        .then((blob) => {
          if (cancelled) {
            resolve()
            return
          }
          const reader = new FileReader()
          reader.onload = (): void => {
            if (!cancelled) ref.current = reader.result as string
            resolve()
          }
          reader.onerror = (): void => {
            if (!cancelled) ref.current = null
            resolve()
          }
          reader.readAsDataURL(blob)
        })
        .catch((error) => {
          console.error('[ShareLogModal] Logo fetch failed:', error)
          if (!cancelled) ref.current = null
          resolve()
        })
    })
    return () => {
      cancelled = true
    }
  }

  useEffect(() => {
    const logoSrc = orgAiSettings?.organization_ai_logo ?? orgAiSettings?.organization_image
    if (!logoSrc || !isOpen) {
      captureLogoDataUrl.current = null
      captureLogoFetchPromise.current = Promise.resolve()
      return
    }
    return fetchAsDataUrl(logoSrc, captureLogoDataUrl, captureLogoFetchPromise)
  }, [isOpen, orgAiSettings])

  useEffect(() => {
    if (!isOpen) {
      capturePoliLogoDataUrl.current = null
      return
    }
    return prefetchAsDataUrl(images.textLogo, capturePoliLogoDataUrl)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      setExportFormat('jpg')
      setPdfUrl(null)
      setPdfBlob(null)
      setJpgZoom(100)
    }
  }, [isOpen])

  const handleDownload = async (): Promise<void> => {
    if (exportFormat === 'pdf' && pdfBlob && pdfUrl) {
      const link = document.createElement('a')
      link.href = pdfUrl
      link.download = `conversation-log-${aiInteractionId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      onClose()
    } else if (
      exportFormat === 'jpg' &&
      conversationRef.current &&
      headerRef.current &&
      footerRef.current
    ) {
      setIsCapturingJpg(true)
      try {
        await captureLogoFetchPromise.current

        const container = conversationRef.current
        const innerContent = container.firstElementChild as HTMLElement
        const header = headerRef.current
        const footer = footerRef.current

        if (!innerContent) {
          setIsCapturingJpg(false)
          return
        }

        const scale = 2
        const scrollTop = container.scrollTop
        const visibleHeight = container.clientHeight
        const visibleWidth = container.clientWidth

        const swapSrc = (img: Element, dataUrl: string | null): void => {
          const el = img as HTMLImageElement
          if (dataUrl) {
            el.src = dataUrl
          } else {
            el.style.display = 'none'
          }
        }
        const prepareImagesForCapture = (doc: Document): void => {
          doc
            .querySelectorAll('img[data-capture="org-logo"]')
            .forEach((img) => swapSrc(img, captureLogoDataUrl.current))
          doc
            .querySelectorAll('img[data-capture="poli-logo"]')
            .forEach((img) => swapSrc(img, capturePoliLogoDataUrl.current))
        }

        const [headerCanvas, fullCanvas, footerCanvas] = await Promise.all([
          html2canvas(header, {
            scale,
            useCORS: true,
            backgroundColor: '#ffffff',
            onclone: prepareImagesForCapture,
          }),
          html2canvas(innerContent, {
            scale,
            useCORS: true,
            backgroundColor: '#f8f8f8',
            onclone: prepareImagesForCapture,
          }),
          html2canvas(footer, {
            scale,
            useCORS: true,
            backgroundColor: '#ffffff',
            onclone: prepareImagesForCapture,
          }),
        ])

        const headerHeight = header.offsetHeight * scale
        const footerHeight = footer.offsetHeight * scale
        const contentHeight = visibleHeight * scale

        const combinedCanvas = document.createElement('canvas')
        combinedCanvas.width = visibleWidth * scale
        combinedCanvas.height = headerHeight + contentHeight + footerHeight

        const ctx = combinedCanvas.getContext('2d')
        if (ctx) {
          ctx.fillStyle = '#f8f8f8'
          ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height)
          ctx.drawImage(headerCanvas, 0, 0)
          ctx.drawImage(
            fullCanvas,
            0,
            scrollTop * scale,
            visibleWidth * scale,
            contentHeight,
            0,
            headerHeight,
            visibleWidth * scale,
            contentHeight,
          )
          ctx.drawImage(footerCanvas, 0, headerHeight + contentHeight)
        }

        combinedCanvas.toBlob(
          (blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.download = `conversation-log-${aiInteractionId}.jpg`
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              URL.revokeObjectURL(url)
            }
            setIsCapturingJpg(false)
            onClose()
          },
          'image/jpeg',
          0.9,
        )
      } catch (error) {
        console.error('Error capturing screenshot:', error)
        setIsCapturingJpg(false)
      }
    }
  }

  const rootClass = `share-log-modal${exportFormat === 'pdf' ? ' share-log-modal--pdf-mode' : ''}`

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      slotProps={{ paper: { className: rootClass } }}
    >
      <div className='share-log-modal__header'>
        <Typography variant='h5' className='share-log-modal__title'>
          Share Conversation Log
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close modal'>
          <Icon name='Close' size={typography.fontSizePx.lg} />
        </IconButton>
      </div>

      {exportFormat === 'pdf' ? (
        <div className='share-log-modal__body'>
          <div className='share-log-modal__pdf-preview'>
            {isPdfGenerating ? (
              <div className='share-log-modal__pdf-preview-centered'>
                <div className='share-log-modal__pdf-loading'>
                  <CircularProgress />
                  <Typography variant='body2' color='textSecondary'>
                    Generating PDF...
                  </Typography>
                </div>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={`${pdfUrl}#toolbar=0`}
                className='share-log-modal__pdf-iframe'
                title='PDF Preview'
              />
            ) : (
              <div className='share-log-modal__pdf-preview-centered'>
                <Typography color='textSecondary'>No preview available</Typography>
              </div>
            )}
          </div>

          <div className='share-log-modal__side-panel'>
            <Typography variant='caption' className='share-log-modal__section-label'>
              Export Format
            </Typography>
            <RadioGroup
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
            >
              <Radio
                value='jpg'
                size='small'
                label='Screenshot (JPG)'
                containerSx={{ m: spacing.none, mb: spacing.pico }}
              />
              <Radio
                value='pdf'
                size='small'
                label='Document (PDF)'
                containerSx={{ m: spacing.none }}
              />
            </RadioGroup>
            {transcript && (
              <>
                <div className='share-log-modal__divider' />
                <div className='share-log-modal__info-box'>
                  <Typography variant='caption' color='textSecondary'>
                    Conversation Info
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{ display: 'block' }}
                    className='share-log-modal__info-item--first'
                  >
                    <strong>Org:</strong> {transcript.organization_name || 'Unknown'}
                  </Typography>
                  <Typography variant='caption' sx={{ display: 'block' }}>
                    <strong>Messages:</strong>{' '}
                    {transcript.queries.length + transcript.responses.length}
                  </Typography>
                  {transcript.created && (
                    <Typography variant='caption' sx={{ display: 'block' }}>
                      <strong>Date:</strong>{' '}
                      {new Date(transcript.created).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Typography>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className='share-log-modal__body'>
          <div className='share-log-modal__chat-card'>
            <div
              ref={headerRef}
              className='share-log-modal__chat-header'
              style={{ backgroundColor: userBgColor }}
            >
              <Avatar
                src={
                  orgAiSettings?.organization_ai_logo ??
                  orgAiSettings?.organization_image ??
                  undefined
                }
                alt={(transcript?.organization_name || 'O').charAt(0).toUpperCase()}
                imgProps={
                  { 'data-capture': 'org-logo' } as React.ImgHTMLAttributes<HTMLImageElement>
                }
                className='share-log-modal__chat-header-avatar'
                style={{ color: userTextColor }}
              >
                {(transcript?.organization_name || 'O').charAt(0).toUpperCase()}
              </Avatar>
              <span
                className='share-log-modal__chat-header-name'
                style={{ color: userTextColor, fontFamily }}
              >
                {orgAiSettings?.chat_name ||
                  `Chat with ${transcript?.organization_name || 'Agent'}`}
              </span>
              <div
                className='share-log-modal__chat-header-meta'
                style={{ color: `${userTextColor}cc`, fontFamily }}
              >
                {transcript?.created && (
                  <span className='share-log-modal__chat-header-date'>
                    {new Date(transcript.created).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}{' '}
                    at{' '}
                    {new Date(transcript.created).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </span>
                )}
                {transcript?.interaction_type && (
                  <span className='share-log-modal__chat-header-type'>
                    <InteractionTypeIcon
                      type={transcript.interaction_type}
                      size={typography.fontSizePx.micro}
                      color='currentColor'
                      showTooltip={false}
                    />
                    {getDisplayAttributes(transcript.interaction_type)[0]}
                  </span>
                )}
              </div>
            </div>

            <div ref={conversationRef} className='share-log-modal__messages'>
              <div className='share-log-modal__messages-inner'>
                {messages.map((message, index) => {
                  const isUser = message.sender === 'USER'
                  const label = isUser ? 'User' : transcript?.organization_name || 'AGENT'
                  const zoomScale = jpgZoom / 100
                  return (
                    <div
                      key={`${message.id}-${index}`}
                      className={`share-log-modal__message share-log-modal__message--${isUser ? 'user' : 'agent'}`}
                      style={
                        {
                          '--bubble-bg': isUser ? userBgColor : '#ffffff',
                          '--bubble-color': isUser ? userTextColor : undefined,
                          '--bubble-radius': cornerRadius,
                          '--bubble-font': fontFamily,
                          '--link-color': linkColor,
                          '--zoom': zoomScale,
                        } as React.CSSProperties
                      }
                    >
                      <Paper elevation={0} className='share-log-modal__message-bubble'>
                        <span className='share-log-modal__message-label'>{label}</span>
                        <div
                          className='share-log-modal__message-body'
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(message.text),
                          }}
                        />
                      </Paper>
                      <span className='share-log-modal__message-time'>
                        {message.timestamp.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}{' '}
                        at{' '}
                        {message.timestamp.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>

            <div ref={footerRef} className='share-log-modal__chat-footer'>
              <img
                src={images.textLogo}
                alt='Polimorphic'
                data-capture='poli-logo'
                className='share-log-modal__poli-logo'
              />
              <span className='share-log-modal__chat-footer-text'>Built by Polimorphic.</span>
            </div>
          </div>

          <div className='share-log-modal__side-panel'>
            <Typography variant='caption' className='share-log-modal__section-label'>
              Export Format
            </Typography>
            <RadioGroup
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
            >
              <Radio
                value='jpg'
                size='small'
                label='Screenshot (JPG)'
                containerSx={{ m: spacing.none, mb: spacing.pico }}
              />
              <Radio
                value='pdf'
                size='small'
                label='Document (PDF)'
                containerSx={{ m: spacing.none }}
              />
            </RadioGroup>
            <div className='share-log-modal__divider' />
            <Typography variant='caption' className='share-log-modal__section-label'>
              Zoom
            </Typography>
            <div className='share-log-modal__zoom-control'>
              <button
                className='share-log-modal__zoom-btn share-log-modal__zoom-btn--minus'
                onClick={() => setJpgZoom((prev) => Math.max(70, prev - 10))}
                disabled={jpgZoom <= 70}
              >
                −
              </button>
              <Typography
                variant='subtitle2'
                component='span'
                className='share-log-modal__zoom-value'
              >
                {jpgZoom}%
              </Typography>
              <button
                className='share-log-modal__zoom-btn share-log-modal__zoom-btn--plus'
                onClick={() => setJpgZoom((prev) => Math.min(100, prev + 10))}
                disabled={jpgZoom >= 100}
              >
                +
              </button>
            </div>
            <Typography variant='caption' className='share-log-modal__help-text'>
              Scroll to position, then click download to capture log
            </Typography>
          </div>
        </div>
      )}

      <div className='share-log-modal__footer'>
        <Button variant='outlined' size='medium' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          size='medium'
          onClick={handleDownload}
          disabled={
            exportFormat === 'pdf'
              ? isPdfGenerating || !pdfBlob
              : isCapturingJpg || messages.length === 0
          }
        >
          {exportFormat === 'jpg' && isCapturingJpg ? 'Capturing...' : 'Download'}
        </Button>
      </div>
    </Dialog>
  )
}

export default ShareLogModalContent
